import { QueryClient, QueryFunction } from "@tanstack/react-query";

import type { ErrorResponse } from "@/lib/types";

type ApiErrorPayload = ErrorResponse & {
  feature?: string;
  migration?: string;
  hint?: string;
};

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: ErrorResponse["details"];
  feature?: string;
  migration?: string;
  hint?: string;
  data?: ApiErrorPayload | null;

  constructor(params: {
    status: number;
    message: string;
    code?: string;
    details?: ErrorResponse["details"];
    feature?: string;
    migration?: string;
    hint?: string;
    data?: ApiErrorPayload | null;
  }) {
    super(params.message);
    this.name = "ApiError";
    this.status = params.status;
    this.code = params.code;
    this.details = params.details;
    this.feature = params.feature;
    this.migration = params.migration;
    this.hint = params.hint;
    this.data = params.data;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isFeatureSchemaUnavailableError(
  error: unknown,
): error is ApiError {
  return isApiError(error) && error.code === "feature_schema_unavailable";
}

function tryParseErrorPayload(text: string): ApiErrorPayload | null {
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text) as ApiErrorPayload;
  } catch {
    return null;
  }
}

async function throwIfResNotOk(res: Response) {
  if (res.ok) {
    return;
  }

  const text = (await res.text()) || res.statusText;
  const payload = tryParseErrorPayload(text);
  const message =
    payload?.error ||
    payload?.hint ||
    text ||
    res.statusText ||
    `Request failed with status ${res.status}`;

  throw new ApiError({
    status: res.status,
    message,
    code: payload?.code,
    details: payload?.details,
    feature: payload?.feature,
    migration: payload?.migration,
    hint: payload?.hint,
    data: payload,
  });
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: { signal?: AbortSignal; expectedActorId?: number },
): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
    Expires: "0",
  };

  // Capture the page/journal owner before awaiting CSRF. Another tab can change
  // the shared session cookie while that request is in flight.
  if (options?.expectedActorId !== undefined) {
    if (!Number.isSafeInteger(options.expectedActorId) || options.expectedActorId <= 0) {
      throw new ApiError({ status: 400, code: "ORDER_SIGNATURE_VALIDATION", message: "簽收帳號資料不完整，請重新登入並核對。" });
    }
    headers["X-Expected-Actor-Id"] = String(options.expectedActorId);
  }

  if (data) {
    headers["Content-Type"] = "application/json";
  }

  if (["POST", "PUT", "PATCH", "DELETE"].includes(method.toUpperCase())) {
    try {
      const csrfResponse = await fetch("/api/csrf-token", {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Cache-Control": "no-cache",
        },
      });

      if (csrfResponse.ok) {
        const csrfData = await csrfResponse.json();
        headers["X-CSRF-Token"] = csrfData.csrfToken;
      }
    } catch (csrfError) {
      // Network error fetching CSRF token — proceed without it.
      // Server-side CSRF middleware will reject with 403 if truly required,
      // giving the user a specific error instead of blocking all mutations here.
      console.warn("CSRF token fetch failed, proceeding without token:", csrfError);
    }
  }

  const res = await fetch(url, {
    method,
    signal: options?.signal,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
    mode: "same-origin",
    redirect: "follow",
    cache: "no-store",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";

export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey[0] as string, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
      credentials: "include",
      mode: "same-origin",
      redirect: "follow",
      cache: "no-store",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      // Keep operational data reasonably fresh without refetching on every render.
      staleTime: 30_000,
      // FE-03: 讀取查詢允許最多 2 次重試（指數退避），改善行動裝置網路瞬斷體驗
      // 4xx 客戶端錯誤不重試（401/403/400 皆屬預期，重試無益）
      retry: (failureCount, error) => {
        if (isApiError(error) && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 10_000),
    },
    mutations: {
      retry: false,
    },
  },
});
